/**
 * Configurazione della variante San Giorgio Bluair (`ric-sg`) della pagina fermi impianto.
 */

export const RIC_SG_PLANT_CODE = 'ric-sg'

/**
 * Linee di San Giorgio nell'ordine in cui vanno presentate: prima le tre linee storiche
 * (1, 2, 5), poi le due nuove (3, 4). Vale per i totali per linea e per il filtro zona.
 *
 * È l'unica fonte di verità: da qui derivano sia quali linee sono valide sia il loro ordine.
 */
export const RIC_SG_LINES_DISPLAY_ORDER: number[] = [1, 2, 5, 3, 4]

/** Numero di linee previste per San Giorgio. */
export const RIC_SG_LINES_COUNT = RIC_SG_LINES_DISPLAY_ORDER.length

export interface ZoneOption {
  value: string
  label: string
}

export interface LineZone extends ZoneOption {
  /** Numero di linea, uno di quelli elencati in `RIC_SG_LINES_DISPLAY_ORDER` */
  line: number
}

/** Posizione della linea nell'ordine di presentazione; -1 se la linea non è prevista. */
const displayRank = (line: number): number => RIC_SG_LINES_DISPLAY_ORDER.indexOf(line)

/**
 * Ricava il numero di linea dall'etichetta della zona, es. "Linea 3" -> 3.
 *
 * Il match è volutamente STRETTO: solo etichette della forma "Linea N" (con eventuali spazi
 * o separatori attorno al numero). Il backend di San Giorgio espone anche zone come
 * "Vagliatura linea 3-4" e "Vagliatura linea 1-2-5", che coprono più linee e non sono una
 * linea singola: cercare semplicemente la prima cifra le farebbe passare per Linea 3 e
 * Linea 1, scalzando le linee vere.
 */
const parseLineNumber = (label: string): number | null => {
  const matched = label.trim().match(/^linea\s*[.:-]?\s*(\d+)$/i)
  if (!matched) {
    return null
  }
  const parsed = parseInt(matched[1], 10)
  return displayRank(parsed) !== -1 ? parsed : null
}

/**
 * Riduce le zone restituite dal backend alle sole linee di San Giorgio, nell'ordine di
 * presentazione definito da `RIC_SG_LINES_DISPLAY_ORDER`.
 *
 * Tiene esclusivamente le zone il cui nome è una linea singola: le altre (es. le vagliature
 * che coprono più linee) vengono scartate, come da specifica "come zone solo le Linee 1-5".
 *
 * Non c'è fallback posizionale di proposito. Se le etichette non corrispondono, questa
 * funzione restituisce un elenco vuoto e la pagina mostra zero linee: un errore evidente e
 * diagnosticabile è preferibile a prendere le prime cinque zone qualsiasi, che produrrebbe
 * dati plausibili ma sbagliati.
 */
export const toRicSgLines = (zones: ZoneOption[]): LineZone[] => {
  const seen: { [line: number]: boolean } = {}
  return zones
    .map(zone => ({ zone, line: parseLineNumber(zone.label) }))
    .filter(entry => entry.line !== null)
    .sort((a, b) => displayRank(a.line as number) - displayRank(b.line as number))
    .filter(entry => {
      const line = entry.line as number
      if (seen[line]) {
        return false
      }
      seen[line] = true
      return true
    })
    .slice(0, RIC_SG_LINES_COUNT)
    .map(entry => ({ value: entry.zone.value, label: entry.zone.label, line: entry.line as number }))
}

/** Classe CSS della riga per una data linea. Vedi `styles/_index.scss`. */
export const lineRowClassName = (lineNumber: number | null): string =>
  lineNumber === null ? '' : `halt-line-${lineNumber}`

/**
 * Causa "linea disabilitata": `HaltType.ManualStop` (1) con `HaltDetail.LineDisabled` (23),
 * che il PLC espone sul tag `MAN_STP_TYPE.BS_LINE_DBLE`.
 *
 * È una delle causali specifiche di San Giorgio, inserite a mano sul database
 * dell'installazione e non create da migration.
 */
export const RIC_SG_LINE_DISABLED_CAUSE_KEY = '1/23'

/** True se il fermo rappresenta una linea disabilitata, non un fermo vero. */
export const isLineDisabledHalt = (halt: { causeKey?: string | null }): boolean =>
  halt.causeKey === RIC_SG_LINE_DISABLED_CAUSE_KEY

export interface Shift {
  value: string
  /** Ora di inizio, inclusa. */
  startHour: number
  /** Ora di fine, esclusa. Minore di `startHour` nel turno che scavalca la mezzanotte. */
  endHour: number
}

/**
 * I tre turni di San Giorgio. Il terzo scavalca la mezzanotte.
 *
 * Gli stessi orari governano anche il confine della giornata lato backend: la variabile
 * `CUTOFF_HOUR` dell'API va impostata a 6 per `ric-sg`, così l'intervallo del date picker
 * va dalle 06:00 del giorno scelto alle 06:00 del giorno dopo e copre esattamente i tre
 * turni. Cambiare questi orari senza allineare il cutoff lascerebbe fuori parte del turno 3.
 */
export const RIC_SG_SHIFTS: Shift[] = [
  { value: '1', startHour: 6, endHour: 14 },
  { value: '2', startHour: 14, endHour: 22 },
  { value: '3', startHour: 22, endHour: 6 },
]

/** Ora di inizio della giornata, cioè l'inizio del primo turno. Vedi `CUTOFF_HOUR`. */
export const RIC_SG_DAY_START_HOUR = RIC_SG_SHIFTS[0].startHour

const twoDigits = (hour: number): string => (hour < 10 ? `0${hour}` : `${hour}`)

/**
 * Turno a cui appartiene un'ora del giorno, oppure `null` se nessun turno la copre.
 * Con i tre turni attuali la copertura è completa, quindi `null` non si verifica.
 */
export const shiftValueForHour = (hour: number): string | null => {
  const shift = RIC_SG_SHIFTS.filter(
    candidate =>
      candidate.startHour < candidate.endHour
        ? hour >= candidate.startHour && hour < candidate.endHour
        : hour >= candidate.startHour || hour < candidate.endHour
  )[0]
  return shift ? shift.value : null
}

/** Voci del filtro turno, etichettate con l'intervallo orario. */
export const ricSgShiftOptions = (
  t: (key: string, options?: { [key: string]: string }) => string
): ZoneOption[] =>
  RIC_SG_SHIFTS.map(shift => ({
    value: shift.value,
    label: t('plantHalts.list.filter.shiftOption', {
      number: shift.value,
      from: twoDigits(shift.startHour),
      to: twoDigits(shift.endHour),
    }),
  }))
